import { Box } from "@mui/material";

import type { WidgetOfType } from "../schema/widgets";
import { colors } from "../theme/tokens";

import { LogoGrid, LogoStripHeading, LogoTile } from "./LogoStrip";

import type { WidgetProps } from "./types";

// Current Bison customer/partner brands, the social-proof wall on the Welcome
// page. Since 2026-10-26 it is the same logo strip as the marketplace grid
// (./LogoStrip.tsx, the brand's Logo wall: logos on plain white, no cards, no
// outlines); until then each logo sat in a 1px-bordered 5:3 tile. Every logo
// with a brand website opens it in a new tab.
//
// The canonical list reads the trimmed, transparent files on
// logos.bisoncommerce.com/supplier/, the files the website's brand walls use.
// Rooms store their own copy of the list, many with the older Contentful JPGs
// (white ground, 10 to 25 percent built-in padding, two solid square badges),
// so HOSTED swaps a known Contentful file for its hosted twin at render and
// every room gets the clean marks with no data push. A customer without a
// logoUrl falls back to its name set in navy (no third-party logo bitmaps ship
// from this repo).
export function CustomerLogos({ widget }: WidgetProps<"customer_logos">) {
    const { title, description, customers } = widget.props;
    // No logos, no wall: a page drops the canonical wall by storing an empty list.
    if (customers.length === 0) return null;

    return (
        <Box>
            <LogoStripHeading title={title} description={description} />
            <CustomerLogoGrid customers={customers} />
        </Box>
    );
}

// The grid alone, also drawn as a feature row's media (FeatureRows.tsx).
export function CustomerLogoGrid({
    customers,
}: {
    customers: WidgetOfType<"customer_logos">["props"]["customers"];
}) {
    return (
        <LogoGrid count={customers.length}>
            {customers.map((c) => {
                const logoUrl = hostedLogo(c.logoUrl);
                return (
                    <LogoTile
                        key={c.id ?? c.name}
                        name={c.name}
                        logoUrl={logoUrl}
                        href={c.href}
                        scale={SCALE[logoUrl ?? ""] ?? 1}
                        color={c.brandColor ?? colors.navy}
                        fallbackText={c.name}
                    />
                );
            })}
        </LogoGrid>
    );
}

const LOGOS = "https://logos.bisoncommerce.com/supplier";

// Contentful file name -> the hosted file the website uses for that brand.
const HOSTED: Record<string, string> = {
    "tvilum.jpg": `${LOGOS}/tvilum.svg`,
    "jofran.jpg": `${LOGOS}/jofran.png`,
    "coaster.jpg": `${LOGOS}/coaster-co-of-america.png`,
    "liberty_furniture.jpg": `${LOGOS}/liberty-furniture-industries.png`,
    "sauder.jpg": `${LOGOS}/sauder-woodworking.png`,
    "bush.jpg": `${LOGOS}/bush-business-furniture.png`,
    "parker-house-resized.png": `${LOGOS}/parker-house-furniture.png`,
    "meridian-logo-resized-2.png": `${LOGOS}/meridian-furniture.png`,
};

function hostedLogo(url?: string) {
    if (!url?.includes("images.ctfassets.net")) return url;
    const file = url.split("?")[0].split("/").pop() ?? "";
    return HOSTED[file] ?? url;
}

// Height tuning, after the website's brand wall (lib/content.ts
// featuredSuppliers draws Jofran at h-[64px] against h-[36px]): Jofran's mark
// is square, so at the common height it reads far smaller than the wordmarks.
const SCALE: Record<string, number> = {
    [`${LOGOS}/jofran.png`]: 1.6,
};
